import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import { plentyArticlesApi } from "../../../../services/api/plenty/articles";

const ArticlesExportSubTab = () => {
  // State for eval files
  const [evalFiles, setEvalFiles] = useState([]);
  const [evalFilesLoading, setEvalFilesLoading] = useState(false);
  const [evalFilesError, setEvalFilesError] = useState(null);

  // State for collapsible directories
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Map());

  // State for eval file creation
  const [createEvalDialogOpen, setCreateEvalDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [createEvalLoading, setCreateEvalLoading] = useState(false);

  // State for article export
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportParams, setExportParams] = useState({
    format: "xlsx",
    source: "",
    article_type: "",
    sort_by: "stock_value",
    sort_order: "desc",
    latest_only: true,
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load eval files on component mount
  useEffect(() => {
    loadEvalFiles();
  }, []);

  // Toggle functions for collapsible directories
  const toggleYear = (year) => {
    setExpandedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
        // Collapse months when collapsing year
        setExpandedMonths((prevMonths) => {
          const newMap = new Map(prevMonths);
          newMap.delete(year);
          return newMap;
        });
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  const toggleMonth = (year, month) => {
    setExpandedMonths((prev) => {
      const newMap = new Map(prev);
      if (!newMap.has(year)) newMap.set(year, new Set());
      const months = newMap.get(year);
      if (months.has(month)) {
        months.delete(month);
      } else {
        months.add(month);
      }
      return newMap;
    });
  };

  // Helper function to transform backend response to flat file array
  const transformEvalFiles = (data) => {
    const files = [];
    if (data.years) {
      for (const year in data.years) {
        // Add year directory
        files.push({
          type: "directory",
          name: year,
          path: `eval/${year}`,
          size: data.years[year].size || null,
          modified: null,
        });
        if (data.years[year].months) {
          for (const month in data.years[year].months) {
            // Add month directory
            files.push({
              type: "directory",
              name: month,
              path: `eval/${year}/${month}`,
              size: data.years[year].months[month].size || null,
              modified: null,
            });
            if (data.years[year].months[month].days) {
              for (const dayObj of data.years[year].months[month].days) {
                // Add file
                files.push({
                  type: "file",
                  name: `eval_${year}_${month}_${dayObj.day}.xlsx`,
                  path: `eval/${year}/${month}/eval_${year}_${month}_${dayObj.day}.xlsx`,
                  size: dayObj.size || null,
                  modified: null,
                });
              }
            }
          }
        }
      }
    }
    return files;
  };

  // Eval Files Management Functions
  const loadEvalFiles = async () => {
    setEvalFilesLoading(true);
    setEvalFilesError(null);
    try {
      const response = await plentyArticlesApi.listEvalFiles();
      const transformedFiles = transformEvalFiles(response.data || {});
      setEvalFiles(transformedFiles);
    } catch (error) {
      console.error("Failed to load eval files:", error);
      setEvalFilesError("Failed to load eval files list");
    } finally {
      setEvalFilesLoading(false);
    }
  };

  const handleCreateEvalFile = async () => {
    setCreateEvalLoading(true);
    try {
      await plentyArticlesApi.createEvalFile(selectedDate);
      setSuccess(`Eval file created successfully for ${selectedDate}`);
      setCreateEvalDialogOpen(false);
      setSelectedDate(() => {
        const today = new Date();
        return today.toISOString().split("T")[0];
      });
      // Reload the files list
      loadEvalFiles();
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error("Failed to create eval file:", error);
      setError(
        `Failed to create eval file: ${error.response?.data?.detail || error.message}`,
      );
    } finally {
      setCreateEvalLoading(false);
    }
  };

  const handleDownloadDailyEval = async (year, month, day) => {
    try {
      const response = await plentyArticlesApi.downloadDailyEval(
        year,
        month,
        day,
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `eval_${year}_${month}_${day}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download daily eval file:", error);
      setError(
        `Failed to download daily eval file: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  const handleDownloadMonthlyEval = async (year, month) => {
    try {
      const response = await plentyArticlesApi.downloadMonthlyEval(year, month);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `eval_${year}_${month}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download monthly eval file:", error);
      setError(
        `Failed to download monthly eval file: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  const handleDownloadYearlyEval = async (year) => {
    try {
      const response = await plentyArticlesApi.downloadYearlyEval(year);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `eval_yearly_${year}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download yearly eval file:", error);
      setError(
        `Failed to download yearly eval file: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  // Article Export Functions
  const handleOpenExportDialog = () => {
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportParams({
      format: "xlsx",
      source: "",
      article_type: "",
      sort_by: "stock_value",
      sort_order: "desc",
      latest_only: true,
    });
  };

  const handleExportParamChange = (field, value) => {
    setExportParams((prev) => ({ ...prev, [field]: value }));
  };

  const handleExportArticles = async () => {
    setExportLoading(true);
    setError(null);

    try {
      // Prepare params, excluding empty strings
      const params = {
        format: exportParams.format,
        sort_by: exportParams.sort_by,
        sort_order: exportParams.sort_order,
        latest_only: exportParams.latest_only,
      };

      if (exportParams.source) params.source = exportParams.source;
      if (exportParams.article_type)
        params.article_type = exportParams.article_type;

      const response = await plentyArticlesApi.exportArticles(params);

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers["content-disposition"];
      let filename = `articles_export.${exportParams.format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      window.URL.revokeObjectURL(url);

      setSuccess(`Export completed successfully: ${filename}`);
      handleCloseExportDialog();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error("Export failed:", error);
      setError(
        `Export failed: ${error.response?.data?.detail || error.message}`,
      );
    } finally {
      setExportLoading(false);
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Articles Export Management
      </Typography>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Eval Files Management Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Eval Files Management
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadEvalFiles}
                    disabled={evalFilesLoading}
                  >
                    {evalFilesLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Refresh"
                    )}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateEvalDialogOpen(true)}
                  >
                    Create Eval File
                  </Button>
                </Box>
              </Box>

              {evalFilesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {evalFilesError}
                </Alert>
              )}

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Path</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evalFilesLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : evalFiles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No eval files found
                        </TableCell>
                      </TableRow>
                    ) : (
                      evalFiles
                        .filter((file) => {
                          if (file.type === "directory") {
                            const parts = file.path.split("/");
                            if (parts.length === 2) return true; // year always visible
                            if (parts.length === 3) {
                              // month
                              const year = parts[1];
                              return expandedYears.has(year);
                            }
                          } else {
                            // file
                            const parts = file.path.split("/");
                            const year = parts[1];
                            const month = parts[2];
                            return (
                              expandedYears.has(year) &&
                              expandedMonths.get(year)?.has(month)
                            );
                          }
                          return true;
                        })
                        .map((file) => (
                          <TableRow key={file.path}>
                            <TableCell>
                              {file.type === "directory" ? (
                                <IconButton
                                  onClick={() => {
                                    const parts = file.path.split("/");
                                    if (parts.length === 2) {
                                      toggleYear(parts[1]);
                                    } else if (parts.length === 3) {
                                      toggleMonth(parts[1], parts[2]);
                                    }
                                  }}
                                >
                                  <FolderIcon color="primary" />
                                </IconButton>
                              ) : (
                                <FileIcon color="action" />
                              )}
                            </TableCell>
                            <TableCell>{file.name}</TableCell>
                            <TableCell>{file.path}</TableCell>
                            <TableCell>
                              {file.size ? formatFileSize(file.size) : "-"}
                            </TableCell>
                            <TableCell>
                              {file.type === "file" &&
                                file.name.endsWith(".xlsx") && (
                                  <Tooltip title="Download File">
                                    <IconButton
                                      onClick={() => {
                                        const parts = file.path.split("/");
                                        const year = parts[parts.length - 3];
                                        const month = parts[parts.length - 2];
                                        const day = file.name
                                          .replace("eval_", "")
                                          .replace(".xlsx", "")
                                          .split("_")[2];
                                        handleDownloadDailyEval(
                                          year,
                                          month,
                                          day,
                                        );
                                      }}
                                      color="primary"
                                    >
                                      <FileDownloadIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {file.type === "directory" &&
                                file.path.split("/").length === 3 && (
                                  <Tooltip title="Download Monthly Archive">
                                    <IconButton
                                      onClick={() => {
                                        const parts = file.path.split("/");
                                        const year = parts[1];
                                        const month = parts[2];
                                        handleDownloadMonthlyEval(year, month);
                                      }}
                                      color="primary"
                                    >
                                      <FileDownloadIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {file.type === "directory" &&
                                file.path.split("/").length === 2 && (
                                  <Tooltip title="Download Yearly Archive">
                                    <IconButton
                                      onClick={() => {
                                        const parts = file.path.split("/");
                                        const year = parts[1];
                                        handleDownloadYearlyEval(year);
                                      }}
                                      color="primary"
                                    >
                                      <FileDownloadIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Article Export Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Article Export
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export articles data with customizable filters and sorting
                options.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleOpenExportDialog}
              >
                Export Articles
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Eval File Dialog */}
      <Dialog
        open={createEvalDialogOpen}
        onClose={() => setCreateEvalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Eval File</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            <TextField
              label="Select Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
              }}
              fullWidth
            />
            <Alert severity="info">
              This will create an eval XLSX file for the selected date using the
              latest available snapshot data.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateEvalDialogOpen(false)}
            disabled={createEvalLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateEvalFile}
            variant="contained"
            color="success"
            disabled={createEvalLoading}
            startIcon={
              createEvalLoading ? <CircularProgress size={20} /> : <AddIcon />
            }
          >
            {createEvalLoading ? "Creating..." : "Create Eval File"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Articles Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Articles</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
            {/* Format Selection */}
            <FormControl fullWidth>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={exportParams.format}
                label="Export Format"
                onChange={(e) =>
                  handleExportParamChange("format", e.target.value)
                }
              >
                <MenuItem value="xlsx">Excel (XLSX)</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
              </Select>
            </FormControl>

            {/* Source Filter */}
            <FormControl fullWidth>
              <InputLabel>Source Filter</InputLabel>
              <Select
                value={exportParams.source}
                label="Source Filter"
                onChange={(e) =>
                  handleExportParamChange("source", e.target.value)
                }
              >
                <MenuItem value="">All Sources</MenuItem>
                <MenuItem value="plenty">Plenty Only</MenuItem>
                <MenuItem value="amazon">Amazon Only</MenuItem>
              </Select>
            </FormControl>

            {/* Article Type Filter */}
            <FormControl fullWidth>
              <InputLabel>Article Type Filter</InputLabel>
              <Select
                value={exportParams.article_type}
                label="Article Type Filter"
                onChange={(e) =>
                  handleExportParamChange("article_type", e.target.value)
                }
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="penner">Penner</MenuItem>
                <MenuItem value="renner">Renner</MenuItem>
                <MenuItem value="neutral">Neutral</MenuItem>
                <MenuItem value="oos">Out of Stock (OOS)</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By */}
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={exportParams.sort_by}
                label="Sort By"
                onChange={(e) =>
                  handleExportParamChange("sort_by", e.target.value)
                }
              >
                <MenuItem value="stock_value">Stock Value</MenuItem>
                <MenuItem value="sku">SKU</MenuItem>
                <MenuItem value="articlename">Article Name</MenuItem>
                <MenuItem value="bi_netto_stock">Stock Quantity</MenuItem>
                <MenuItem value="bi_selled_articles">Sales (Last 30d)</MenuItem>
                <MenuItem value="gross_revenue">Gross Revenue</MenuItem>
                <MenuItem value="ratio">Ratio</MenuItem>
                <MenuItem value="snapshot_date">Snapshot Date</MenuItem>
              </Select>
            </FormControl>

            {/* Sort Order */}
            <FormControl fullWidth>
              <InputLabel>Sort Order</InputLabel>
              <Select
                value={exportParams.sort_order}
                label="Sort Order"
                onChange={(e) =>
                  handleExportParamChange("sort_order", e.target.value)
                }
              >
                <MenuItem value="desc">Descending (High to Low)</MenuItem>
                <MenuItem value="asc">Ascending (Low to High)</MenuItem>
              </Select>
            </FormControl>

            {/* Latest Only Toggle */}
            <FormControl fullWidth>
              <InputLabel>Data Scope</InputLabel>
              <Select
                value={exportParams.latest_only}
                label="Data Scope"
                onChange={(e) =>
                  handleExportParamChange("latest_only", e.target.value)
                }
              >
                <MenuItem value={true}>Latest Snapshots Only</MenuItem>
                <MenuItem value={false}>All Historical Data</MenuItem>
              </Select>
            </FormControl>

            {/* Info Box */}
            <Alert severity="info">
              <Typography variant="body2">
                Export will include: SKU, Article ID, Variant ID, Article Name,
                Type, Source, Stock, Sales, Ratio, Revenue, Stock Value, ROI,
                ASIN, FNSKU, and timestamps.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} disabled={exportLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleExportArticles}
            variant="contained"
            color="success"
            disabled={exportLoading}
            startIcon={
              exportLoading ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon />
              )
            }
          >
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticlesExportSubTab;
